import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import DashboardPage from "./Pages/DashboardPage";
import UsersPage from "./Pages/UsersPage";
import SettingsPage from "./Pages/SettingsPage";

import { ReactComponent as CluvaLogoIcon } from "../../../assets/icons/svg/Dashboard sidebar/Cluva_logo.svg";
import { ReactComponent as DashboardIcon } from "../../../assets/icons/svg/Dashboard sidebar/Dashboard.svg";
import { ReactComponent as AllUserIcon } from "../../../assets/icons/svg/Dashboard sidebar/All_users.svg";
import { ReactComponent as SettingIcon } from "../../../assets/icons/svg/Dashboard sidebar/Setting.svg";
import AuthUtils from "../../utils/auth.utils";
import { API_URLS } from "../../api/urls";

interface AdminDetails {
  id: number;
  admin_name: string;
  email: string;
  admin_mobile: string;
  admin_profile: string | null;
  created_at: string;
}

const DashboardScreen: React.FC = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState("dashboard");
  const [adminDetails, setAdminDetails] = useState<AdminDetails | null>(null);
  const [adminLoading, setAdminLoading] = useState(true);

  // Fetch admin details
  useEffect(() => {
    const fetchAdminDetails = async () => {
      try {
        setAdminLoading(true);
        const adminId = AuthUtils.getAdminId();
        if (!adminId) {
          navigate('/login', { replace: true });
          return;
        }

        const response = await fetch(API_URLS.GET_ADMIN_DETAILS(Number(adminId)));
        const apiData = await response.json();

        console.log('Admin Details Response:', apiData); // Debug log

        // The API returns admin details directly at root level, not wrapped in data property
        if (apiData && apiData.id) {
          console.log('Setting admin details:', apiData); // Debug log
          setAdminDetails(apiData);
        } else {
          console.error('Failed to fetch admin details:', apiData);
        }
      } catch (err) {
        console.error('Error fetching admin details:', err);
      } finally {
        setAdminLoading(false);
      }
    };

    fetchAdminDetails();
  }, [navigate]);

  // Get route from pathname on mount and when URL changes
  useEffect(() => {
    const getRouteFromPath = () => {
      const path = window.location.pathname;
      if (path === '/users') return 'users';
      if (path === '/settings') return 'settings';
      return 'dashboard';
    };

    const handlePopState = () => {
      const route = getRouteFromPath();
      setSelected(route);
    };

    // Set initial route
    const initialRoute = getRouteFromPath();
    setSelected(initialRoute);

    // Listen for browser back/forward
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Session check interval
  useEffect(() => {
    // Check session validity every minute
    const sessionCheckInterval = setInterval(() => {
      if (!AuthUtils.isSessionValid()) {
        // Session expired, redirect to login
        navigate('/login', { replace: true });
      }
    }, 60000); // Check every minute

    return () => {
      clearInterval(sessionCheckInterval);
    };
  }, [navigate]);

  const navigateTo = (route: string) => {
    const path = route === 'dashboard' ? '/' : `/${route}`;
    window.history.pushState({}, '', path);
    setSelected(route);
  };

  // Get profile image URL
  const getProfileImageUrl = () => {
    if (!adminDetails?.admin_profile) {
      console.log('No admin profile found'); // Debug log
      return null;
    }
    if (adminDetails.admin_profile.startsWith('http')) {
      console.log('Using full URL:', adminDetails.admin_profile); // Debug log
      return adminDetails.admin_profile;
    }
    const fullUrl = `https://api.feebdack.in${adminDetails.admin_profile}`;
    console.log('Using relative URL:', fullUrl); // Debug log
    return fullUrl;
  };

  const profileImageUrl = getProfileImageUrl();

  console.log('Admin Details State:', adminDetails); // Debug log
  console.log('Profile Image URL:', profileImageUrl); // Debug log
  console.log('Admin Loading:', adminLoading); // Debug log

  return (
    <div className="bg-gray-50 relative h-screen overflow-hidden">
      <div className="flex h-full">
        {/* Sidebar */}
        <div className="w-20 h-screen bg-white border-r border-gray-200">
          <div className="flex flex-col justify-between h-full items-center">
            <div>
              {/* Logo */}
              <div className="w-10 h-10 mx-auto mt-6 rounded-lg flex items-center justify-center">
                <CluvaLogoIcon className={`w-[46px] h-[46px]`} />
              </div>

              {/* Divider */}
              <div className="w-7 h-0.5 bg-gray-200 mx-auto mt-4"></div>

              {/* Navigation Icons */}
              <div
                className={`h-12 w-12 flex items-center justify-center rounded-xl cursor-pointer mx-auto mt-6 transition-colors ${
                  selected === "dashboard" ? "bg-gray-100" : "hover:bg-gray-50"
                }`}
                onClick={() => navigateTo('dashboard')}
              >
                <DashboardIcon className={`w-[24px] h-[24px]`} />
              </div>

              <div
                className={`h-12 w-12 flex items-center justify-center rounded-xl cursor-pointer mx-auto mt-4 transition-colors ${
                  selected === "users" ? "bg-gray-100" : "hover:bg-gray-50"
                }`}
                onClick={() => navigateTo('users')}
              >
                <AllUserIcon className={`w-[24px] h-[24px]`} />
              </div>

              <div
                className={`h-12 w-12 flex items-center justify-center rounded-xl cursor-pointer mx-auto mt-4 transition-colors ${
                  selected === "settings" ? "bg-gray-100" : "hover:bg-gray-50"
                }`}
                onClick={() => navigateTo('settings')}
              >
                <SettingIcon className={`w-[24px] h-[24px]`} />
              </div>
            </div>

            {/* Bottom Section with Avatar only */}
            <div className="mb-5 flex flex-col items-center">
              {/* Avatar */}
              <div 
                className="w-10 h-10 rounded-full bg-orange-200 flex items-center justify-center text-2xl overflow-hidden"
                title={adminDetails?.admin_name || "Admin"}
              >
                {adminLoading ? (
                  <div className="animate-pulse">👤</div>
                ) : profileImageUrl ? (
                  <img 
                    src={profileImageUrl} 
                    alt={adminDetails?.admin_name || "Admin"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error('Image failed to load:', profileImageUrl); // Debug log
                      // Fallback to emoji if image fails to load
                      e.currentTarget.style.display = 'none';
                      if (e.currentTarget.parentElement) {
                        e.currentTarget.parentElement.textContent = '👤';
                      }
                    }}
                    onLoad={() => {
                      console.log('Image loaded successfully:', profileImageUrl); // Debug log
                    }}
                  />
                ) : (
                  '👤'
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto h-full">
          <div style={{ display: selected === "dashboard" ? "block" : "none" }}>
            <DashboardPage adminName={adminDetails?.admin_name} adminLoading={adminLoading} />
          </div>

          <div style={{ display: selected === "users" ? "block" : "none" }}>
            <UsersPage />
          </div>

          <div style={{ display: selected === "settings" ? "block" : "none" }}>
            <SettingsPage />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardScreen;